/**
 * A client's own days for the CSP's billing dates (Nikhil 2026-10-28: the billing dates screen shows and edits the cycle
 * dates as the client's days, labelled with the client's zone, so the screen and the CS Analysis assistant say the
 * same day for a client anywhere). The zone is the client's IANA zone as CS Analysis keeps it (Client.timezone, from
 * the store's country / city); the same rules as packages/shared in infurnia-cs-analysis (account-status.ts billDay,
 * time-zone.ts dayIn).
 *
 * Every function returns null for a zone the browser does not know, and the caller then says so.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The calendar fields of an instant in a zone, or null (an unknown zone, an invalid instant). */
function fields_in(instant, time_zone) {
    const d = instant instanceof Date ? instant : new Date(instant);
    if (!time_zone || Number.isNaN(d.getTime())) return null;
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone: time_zone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' }).formatToParts(d);
        const get = (type) => Number(parts.find((p) => p.type === type).value);
        return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute'), second: get('second') };
    } catch (e) {
        return null;
    }
}

/** True when the browser knows the zone. */
export function is_known_time_zone(time_zone) {
    return fields_in(new Date(0), time_zone) !== null;
}

/** The day of an instant in the client's zone, as the screen prints a day ("Oct 02 2026"), or null. */
export function client_day(instant, time_zone) {
    const f = fields_in(instant, time_zone);
    return f ? `${MONTHS[f.month - 1]} ${String(f.day).padStart(2, '0')} ${f.year}` : null;
}

/**
 * The bill day of a billing date in the client's zone, or null. infurnia-main stores a cycle's end as its last second
 * (nearly always 23:59:59 UTC) and bills at the next second, so the bill day is the day that next second falls on:
 * 2026-10-01T23:59:59Z is Oct 02 in Kolkata and London, Oct 01 in New York.
 */
export function client_bill_day(instant, time_zone) {
    const d = instant instanceof Date ? instant : new Date(instant);
    return client_day(new Date(d.getTime() + 1000), time_zone);
}

/**
 * The instant a day starts in the client's zone (ISO), or null: the value the screen sends for a date picked as the
 * client's day. `month` is 1-12. For a client in Asia/Kolkata it is the instant an Indian browser sends today (00:00
 * IST). On a day whose midnight a daylight-saving change skips it is the day's first instant (01:00).
 */
export function client_day_start(year, month, day, time_zone) {
    if (!is_known_time_zone(time_zone)) return null;
    const HOUR = 60 * 60 * 1000;
    const target = Date.UTC(year, month - 1, day);
    const offset_at = (t) => {
        const f = fields_in(new Date(t), time_zone);
        return Date.UTC(f.year, f.month - 1, f.day, f.hour, f.minute, f.second) - t;
    };
    // The zone's offset before and after any change near that midnight (a zone is at most 14 h from UTC): midnight
    // read with each; the earliest that falls on the day asked is its start.
    const starts = [target - offset_at(target - 14 * HOUR), target - offset_at(target + 14 * HOUR)]
        .filter((t) => {
            const f = fields_in(new Date(t), time_zone);
            return f.year === year && f.month === month && f.day === day;
        })
        .sort((a, b) => a - b);
    return starts.length ? new Date(starts[0]).toISOString() : null;
}
